import { Alert, Box, Button, Drawer, Stack, styled, TextField, Typography } from "@mui/material";
import { DialogHeader, DrawerContent, DrawerHeader, StyledDivider } from "./GuestCheckoutCommon";
import { LinkButton } from "~/common/components/Link";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { useLoginApi } from "~/Login/components/LoginPopup";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { CART_STEP_3 } from "~/utils/url/staticUrls";
import { Dialog, DialogContent, DialogTitle } from "~/common/m3/Dialog/Dialog";

export type GuestCheckoutLoginModalProps = {
  open: boolean;
  onClose: () => void;
  onContinueAsGuest: () => void;
  onResetPassword: (email: string) => void;
};

interface LoginDataForm {
  login: string;
  password: string;
}

export const GuestCheckoutLoginModal = ({
  open,
  onClose,
  onContinueAsGuest,
  onResetPassword: onResetPasswordExternal,
}: GuestCheckoutLoginModalProps) => {
  const renderDialog = useIsMinTabletSize();
  const { t } = useTranslation();

  const { handleSubmit, clearErrors, reset, getValues, setError, control } = useForm<LoginDataForm>();
  const { login, error, inFlight } = useLoginApi({
    alternativeLoginAction: () => redirectToUrl(CART_STEP_3),
  });

  const onSubmit = handleSubmit(async (data) => {
    clearErrors();
    const result = await login(data.login, data.password);
    if (!result) {
      setError("login", { type: "manual", message: "wrong.data" });
      setError("password", { type: "manual", message: "wrong.data" });
    }
  });

  const onResetPassword = () => {
    const email = getValues("login");
    reset();
    onResetPasswordExternal(email);
  };

  const content = (
    <Content>
      {/* Guest side */}
      <Stack gap={3}>
        <Stack gap={1}>
          <DialogTitle>{t("feature.guestCheckout.title")}</DialogTitle>
          <Typography variant="bodyLarge">{t("feature.guestCheckout.description")}</Typography>
        </Stack>
        <Button variant="contained" color="primary" onClick={onContinueAsGuest}>
          {t("feature.guestCheckout.title")}
        </Button>
      </Stack>
      {/* Divider with or */}
      <StyledDivider orientation={renderDialog ? "vertical" : "horizontal"} flexItem>
        {t("common.or")}
      </StyledDivider>
      {/* Sign in side */}
      <form onSubmit={onSubmit}>
        <Box flex={1} display="flex" flexDirection="column" justifyContent="center">
          <DialogTitle mb={3}>{t("feature.guestCheckout.login.title")}</DialogTitle>
          <Stack gap={2} mb={1}>
            <Controller
              name="login"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <TextField label={t("page.completeRegistration.email")} fullWidth {...field} error={!!error} />
              )}
            />
            <Controller
              name="password"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label={t("page.completeRegistration.password")}
                  variant="outlined"
                  fullWidth
                  type="password"
                  error={!!error}
                  {...field}
                />
              )}
            />
          </Stack>
          {error && (
            <Alert className={"my-2"} severity={"error"}>
              {t(error)}
            </Alert>
          )}
          <Stack direction="row" justifyContent="flex-end" alignItems="center" mb={2} gap={1}>
            <Typography variant="labelLarge">{t("feature.guestCheckout.login.lostPassword")}</Typography>
            <LinkButton onClick={onResetPassword}>{t("forgottenPwd.expired.buttontext")}</LinkButton>
          </Stack>
          <Button variant="contained" color="secondary" fullWidth type="submit" loading={inFlight}>
            {t("feature.guestCheckout.login.loginBtn")}
          </Button>
        </Box>
      </form>
    </Content>
  );

  return renderDialog ? (
    <Dialog open={open} onClose={onClose} maxWidth={"desktop-s"}>
      <DialogHeader onClose={onClose} />
      <DialogContent>{content}</DialogContent>
    </Dialog>
  ) : (
    <Drawer anchor="bottom" open={open} onClose={onClose}>
      <DrawerHeader onClose={onClose} />
      <DrawerContent>{content}</DrawerContent>
    </Drawer>
  );
};

const Content = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),

  [theme.breakpoints.up("desktop-s")]: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "stretch",
  },
}));
